import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

// Styles
import "./assets/styles/style.scss";

function Analytics({ baseDomain }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    let isMounted = true;

    const fetchUsage = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${baseDomain}/getUsage`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (isMounted) {
          setData(response.data);
        }
      } catch (error) {
        window.location.href = "/login";
        console.log(error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchUsage();

    return () => {
      isMounted = false;
    };
  }, [baseDomain, token]);

  const perModel = useMemo(() => {
    if (!data?.entries) return [];
    const map = new Map();

    data.entries.forEach((entry) => {
      const key = entry.model || "unknown";
      if (!map.has(key)) {
        map.set(key, {
          tokens: 0,
          cost: 0,
          inputCost: 0,
          outputCost: 0,
          calls: 0,
        });
      }
      const agg = map.get(key);
      agg.tokens += entry.totalTokens || 0;
      agg.cost += entry.cost || 0;
      agg.inputCost += entry.inputCost || 0;
      agg.outputCost += entry.outputCost || 0;
      agg.calls += 1;
    });

    return Array.from(map.entries())
      .map(([model, agg]) => ({ model, ...agg }))
      .sort((a, b) => b.cost - a.cost);
  }, [data]);

  const formatNumber = (value) => {
    if (typeof value === "number" && !Number.isNaN(value)) {
      return value.toLocaleString("en-US");
    }
    return "0";
  };

  const formatMoney = (value) => {
    if (typeof value === "number" && !Number.isNaN(value)) {
      return `€${value.toFixed(4)}`;
    }
    return "€0.0000";
  };

  return (
    <div className="App p-5 admin">
      <div className="text-center">
        <div className="d-flex justify-content-center mb-3">
          <Link to="/admin" className="btn btn-outline-primary">
            Stories
          </Link>
        </div>
        {isLoading ? (
          <h1>LOADING ...</h1>
        ) : (
          <>
            <h1 className="mb-4">OpenAI usage analytics</h1>

            {data && (
              <div className="row g-3 mb-4 text-start">
                <StatCard label="API calls" value={data.entries.length} />
                <StatCard
                  label="Total tokens"
                  value={formatNumber(data.totals.tokens)}
                />
                <StatCard
                  label="Input tokens"
                  value={formatNumber(data.totals.inputTokens)}
                />
                <StatCard
                  label="Output tokens"
                  value={formatNumber(data.totals.outputTokens)}
                />
                <StatCard
                  label="Total cost (€)"
                  value={formatMoney(data.totals.cost)}
                />
                <StatCard
                  label="Input cost (€)"
                  value={formatMoney(data.totals.inputCost)}
                />
                <StatCard
                  label="Output cost (€)"
                  value={formatMoney(data.totals.outputCost)}
                />
                <StatCard
                  label="Avg cost per 1k tokens"
                  value={
                    data.totals.tokens > 0
                      ? formatMoney(
                          (data.totals.cost / data.totals.tokens) * 1000
                        )
                      : "—"
                  }
                />
              </div>
            )}

            {perModel.length > 0 && (
              <div className="mb-5 text-start">
                <h2>Usage by model</h2>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Model</th>
                      <th className="text-end">Calls</th>
                      <th className="text-end">Tokens</th>
                      <th className="text-end">Input €</th>
                      <th className="text-end">Output €</th>
                      <th className="text-end">Total €</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perModel.map((row) => (
                      <tr key={row.model}>
                        <td>{row.model}</td>
                        <td className="text-end">{row.calls}</td>
                        <td className="text-end">
                          {formatNumber(row.tokens)}
                        </td>
                        <td className="text-end">
                          {formatMoney(row.inputCost)}
                        </td>
                        <td className="text-end">
                          {formatMoney(row.outputCost)}
                        </td>
                        <td className="text-end">{formatMoney(row.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data && data.entries.length > 0 ? (
              <div className="text-start">
                <h2>All requests</h2>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Model</th>
                        <th className="text-end">In tokens</th>
                        <th className="text-end">Out tokens</th>
                        <th className="text-end">Total tokens</th>
                        <th className="text-end">Input €</th>
                        <th className="text-end">Output €</th>
                        <th className="text-end">Total €</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.entries
                        .slice()
                        .reverse()
                        .map((entry, idx) => (
                          <tr key={idx}>
                            <td>
                              {new Date(entry.timestamp).toLocaleString()}
                            </td>
                            <td>{entry.model}</td>
                            <td className="text-end">{entry.inputTokens}</td>
                            <td className="text-end">{entry.outputTokens}</td>
                            <td className="text-end">{entry.totalTokens}</td>
                            <td className="text-end">
                              {formatMoney(entry.inputCost)}
                            </td>
                            <td className="text-end">
                              {formatMoney(entry.outputCost || 0)}
                            </td>
                            <td className="text-end">
                              {formatMoney(entry.cost || 0)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p>No usage logged yet.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="col-12 col-md-6 col-xl-3">
      <div className="border rounded p-3 h-100 bg-white">
        <div className="small text-muted">{label}</div>
        <div className="fs-5">{value}</div>
      </div>
    </div>
  );
}

export default Analytics;
